/**
 * useAuth0Safe — thin wrapper around @auth0/auth0-react.
 *
 * loginWithRedirect ALWAYS routes the visitor to the branded `/prijava`
 * wrapper page (see PrijavaPage.js) instead of jumping straight to the
 * raw Auth0 universal-login. Callers can pass `auth0Direct: true` to skip
 * the wrapper (used internally by PrijavaPage itself).
 *
 * When the Auth0 tenant is NOT configured (flag set in index.js), it
 * still routes the visitor to /prijava so they see a friendly screen
 * (the page itself shows a soft toast if Auth0 cannot be invoked).
 *
 * Pre-existing app-state passing (returnTo, etc.) is preserved by storing
 * it in sessionStorage so PrijavaPage can replay it onto loginWithRedirect.
 */
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const APP_STATE_KEY = 'emina_prijava_intent';
const LOCAL_ADMIN_KEY = 'emina_local_admin_token';

export function useAuth0Safe() {
  const a0 = useAuth0();
  const navigate = useNavigate();
  const configured = typeof window !== 'undefined' && window.__EMINA_AUTH0_CONFIGURED__;

  // Honour a local admin JWT (also used in admin pages). Pages that
  // require any logged-in user — like /moji-oglasi — can therefore work
  // for both Auth0 users and the seeded local admin without requiring
  // a separate auth path.
  const localToken = typeof window !== 'undefined' ? localStorage.getItem(LOCAL_ADMIN_KEY) : null;
  if (localToken && !a0.isAuthenticated) {
    let payload = {};
    try { payload = JSON.parse(atob(localToken.split('.')[1] || '')); } catch {}
    return {
      ...a0,
      isAuthenticated: true,
      isLoading: false,
      user: { email: payload.email, name: payload.name, sub: payload.sub, picture: '' },
      getAccessTokenSilently: async () => localToken,
      loginWithRedirect: () => navigate('/prijava'),
      logout: () => { try { localStorage.removeItem(LOCAL_ADMIN_KEY); } catch {} navigate('/'); },
    };
  }

  return {
    ...a0,
    loginWithRedirect: (opts) => {
      // Internal callers (PrijavaPage) can opt-in to direct Auth0 redirect.
      if (opts && opts.auth0Direct) {
        if (!configured) {
          toast.info('Korisnička prijava trenutno nije aktivirana. Provjerite uskoro!');
          return Promise.resolve();
        }
        const { auth0Direct, ...rest } = opts;
        return a0.loginWithRedirect(rest);
      }
      // Save the intent so /prijava can preserve returnTo / screen_hint.
      try {
        if (opts) sessionStorage.setItem(APP_STATE_KEY, JSON.stringify(opts));
        else sessionStorage.removeItem(APP_STATE_KEY);
      } catch {}
      // Universal: every public Prijava click → branded /prijava wrapper.
      navigate('/prijava');
      return Promise.resolve();
    },
    logout: (opts) => {
      if (!configured) {
        navigate('/');
        return;
      }
      return a0.logout(opts);
    },
  };
}

export const PRIJAVA_INTENT_KEY = APP_STATE_KEY;
